//import React from 'react';
import routes from 'routes';
import { UserPermissionLevel } from 'constants/auth';

// The order (Doug, 2026-10-26, school words pass 1): the people pages together, then Meets. A
// club reads Home, My Athlete Results, My Volunteer Status, Members, Athletes, Visitors, Meets,
// Club Records, Reports, Settings; a school reads Houses where a club reads Visitors, and School
// Records.
export const links = [
  {
    name: 'Home',
    url: routes.admin.dashboard,
    icon: '🏠',
  },
  {
    name: 'My Athlete Results',
    url: routes.admin.athleteDashboard,
    icon: '📊',
  },
  {
    name: 'My Volunteer Status',
    url: routes.admin.volunteerStatus,
    icon: '🙋‍♂️',
  },
  {
    // Members and Athletes are entries of their own, no longer inside a Members group (Doug,
    // 2026-10-26).
    name: 'Members',
    url: routes.admin.members.members,
    icon: '👥',
    permissionLevel: UserPermissionLevel.Official,
  },
  {
    name: 'Athletes',
    url: routes.admin.members.athletes,
    icon: '🏃‍♂️',
    permissionLevel: UserPermissionLevel.Official,
  },
  {
    // Shown only to a club that has teams (a school): `feature` names an
    // entry of clubFeaturesOf (utils/clubFeatures.js). `teamWord` names the
    // entry in the club's own word, so it reads Teams, Houses or Factions
    // (utils/teamLabel.js); `name` is only the fallback. Its own entry below
    // Athletes, so it is found at a glance (Doug, 2026-10-16).
    name: 'Teams',
    teamWord: 'teams',
    feature: 'teams',
    url: routes.admin.members.teams,
    icon: '🛡️',
    permissionLevel: UserPermissionLevel.Official,
  },
  // Guests (Guest Members, Guest Athletes) is deliberately unlisted. The
  // feature is built but unused and confusing, so it is hidden for every club
  // (Doug, 2026-10-11). The two pages are still in the code
  // (pages/admin/GuestMembers, pages/admin/GuestAthletes) and their addresses
  // go to the dashboard (routes/RouteConfig.js). To bring the feature back,
  // restore the menu entry here and the two routes there.
  {
    // Shown only to a club that has visitors: `feature` names an entry of
    // clubFeaturesOf (utils/clubFeatures.js). A school has none.
    name: 'Visitors',
    feature: 'visitors',
    url: routes.admin.visitors.members,
    icon: '🎟️',
    permissionLevel: UserPermissionLevel.Official,
    child: [
      {
        name: 'Visitor Members',
        url: routes.admin.visitors.members,
        icon: '👥',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Visitor Athletes',
        url: routes.admin.visitors.athletes,
        icon: '🏃‍♂️',
        permissionLevel: UserPermissionLevel.Official,
      },
    ],
  },
  {
    name: 'Meets',
    url: routes.admin.meets,
    icon: '📅',
  },
  {
    // Named in the organisation's word, followed by organisationWordAfter:
    // Club Records or School Records (utils/clubFeatures.js,
    // organisationTitleOf).
    name: 'Club Records',
    organisationWordAfter: ' Records',
    url: routes.admin.clubRecord,
    icon: '🏆',
    pending: true,
    permissionLevel: UserPermissionLevel.Guest,
  },
  {
    name: 'Reports',
    url: routes.admin.reports.athleteSeasonPoints, // Update parent URL
    icon: '📈',
    permissionLevel: UserPermissionLevel.Official,
    child: [
      {
        name: 'Athlete PRs',
        url: routes.admin.reports.athletePRs,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Athlete Season Points',
        url: routes.admin.reports.athleteSeasonPoints,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        // Each group and gender's top 3 over the season (points slice 6), clubs and schools.
        name: 'Season Champions',
        url: routes.admin.reports.seasonChampions,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        // A school's report (points slice 5), named in its word: House Points,
        // Faction Points or Team Points.
        name: 'House Points',
        teamWord: 'team',
        teamWordAfter: ' Points',
        feature: 'teams',
        url: routes.admin.reports.housePoints,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Athlete Attendance',
        url: routes.admin.reports.athleteAttendance,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Most PRs in a Meet',
        url: routes.admin.reports.mostMeetPRs,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Event Participation',
        url: routes.admin.reports.eventParticipation,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Season Event Performance',
        url: routes.admin.reports.seasonEventPerformance,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Member Volunteer Hours',
        url: routes.admin.reports.memberVolunteerPoints,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Official,
      },
      {
        name: 'Results Sync',
        url: routes.admin.reports.resultsSync,
        icon: '📈',
        permissionLevel: UserPermissionLevel.Admin,
      },
    ],
  },

  {
    // Settings is for Admins and Owners only (Doug, 2026-10-11): the group,
    // every entry in it and every page behind it ask for Admin. Named
    // Settings for every club (Doug, 2026-10-26).
    name: 'Settings',
    url: routes.admin.clubSettings.general,
    icon: '⚙️',
    permissionLevel: UserPermissionLevel.Admin,
    child: [
      {
        name: 'General & Points',
        url: routes.admin.clubSettings.general,
        icon: '⚙️',
        permissionLevel: UserPermissionLevel.Admin,
      },
      {
        // Shown as "Year Groups" for a club that groups by year level:
        // groupWord names the entry of groupWords (utils/groupLabel.js) that
        // MenuLinkList shows in place of name.
        name: 'Age Groups',
        groupWord: 'groups',
        url: routes.admin.clubSettings.ageGroups,
        icon: '⚙️',
        permissionLevel: UserPermissionLevel.Admin,
      },
      // Athlete Groups is deliberately unlisted. The feature (arbitrary
      // athlete groupings assignable to a meet event program) is fully built,
      // but age groups turned out to cover the same need, so it is shelved
      // rather than deleted. The page, route, API and program/volunteer
      // integrations are all kept: the page is still reachable at
      // routes.admin.clubSettings.athleteGroups, and clubs that already have
      // athlete groups keep working — the program designer and volunteer
      // screens only surface athlete groups when a club actually has some.
      // To re-enable, restore the menu entry here.
      {
        name: 'Volunteer Roles',
        url: routes.admin.clubSettings.volunteerRoles,
        icon: '⚙️',
        permissionLevel: UserPermissionLevel.Admin,
      },
      {
        name: 'Volunteer Hours Targets',
        url: routes.admin.clubSettings.volunteerHoursTargets,
        icon: '⚙️',
        permissionLevel: UserPermissionLevel.Admin,
      },
      {
        name: 'Event Types',
        url: routes.admin.clubSettings.eventTypes,
        icon: '⚙️',
        permissionLevel: UserPermissionLevel.Admin,
      },
      {
        name: 'Manage Seasons',
        url: routes.admin.clubSettings.manageSeasons.url('all'),
        icon: '⚙️',
        permissionLevel: UserPermissionLevel.Admin,
      },
    ],
  },
];
